import React, { useState } from 'react';
import { PatientNote } from '../../models/patientNote';
import { timeAgo } from '../insights/insightPresentation';
import NoteMarkdown from './NoteMarkdown';
import { PinIcon, PencilNoteIcon, TrashNoteIcon } from '../../assets/icons';

interface NoteCardProps {
  note: PatientNote;
  canManage: boolean;
  saving: boolean;
  onTogglePin: () => void;
  onSaveEdit: (text: string) => Promise<void>;
  onDelete: () => void;
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  doctor: 'Doctor',
  nutritionist: 'Nutricionista',
};

const actionButtonStyle: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'var(--muted-foreground)',
  cursor: 'pointer',
  padding: 4,
  borderRadius: 6,
  display: 'flex',
  alignItems: 'center',
  lineHeight: 1,
};

const NoteCard: React.FC<NoteCardProps> = ({
  note,
  canManage,
  saving,
  onTogglePin,
  onSaveEdit,
  onDelete,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const startEdit = () => {
    setDraft(note.text);
    setEditing(true);
  };

  const saveEdit = async () => {
    const text = draft.trim();
    if (!text) return;
    try {
      await onSaveEdit(text);
      setEditing(false);
    } catch {
      // Stay in edit mode; the panel surfaces the error banner.
    }
  };

  const edited = note.updatedAt !== note.createdAt;
  const authorName = note.author?.name || 'Autor desconocido';
  const roleLabel = note.author ? ROLE_LABELS[note.author.role] || note.author.role : null;

  return (
    <div
      style={{
        border: note.isPinned ? '1px solid var(--primary-subtle)' : '1px solid var(--border)',
        background: note.isPinned ? 'var(--primary-subtle)' : 'var(--card)',
        borderRadius: 10,
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
        <span
          style={{
            fontWeight: 600,
            fontSize: 13,
            color: 'var(--foreground)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {authorName}
        </span>
        {roleLabel && (
          <span style={{ fontSize: 11, color: 'var(--muted-foreground)', flexShrink: 0 }}>{roleLabel}</span>
        )}
        <span
          title={new Date(note.createdAt).toLocaleString('es-ES')}
          style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--muted-foreground)', flexShrink: 0 }}
        >
          {timeAgo(note.createdAt)}
          {edited && ' (editado)'}
        </span>
      </div>

      {editing ? (
        <>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={4}
            autoFocus
            disabled={saving}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: 8,
              fontSize: 14,
              fontFamily: 'inherit',
              resize: 'vertical',
              outline: 'none',
            }}
          />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              onClick={() => setEditing(false)}
              disabled={saving}
              style={{
                border: '1px solid var(--border)',
                background: 'var(--card)',
                color: 'var(--foreground)',
                borderRadius: 6,
                padding: '5px 12px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              onClick={saveEdit}
              disabled={saving || !draft.trim()}
              style={{
                border: 'none',
                background: 'var(--primary)',
                color: 'var(--card)',
                borderRadius: 6,
                padding: '5px 12px',
                fontSize: 13,
                fontWeight: 600,
                cursor: saving || !draft.trim() ? 'default' : 'pointer',
                opacity: saving || !draft.trim() ? 0.6 : 1,
              }}
            >
              Guardar
            </button>
          </div>
        </>
      ) : (
        <div style={{ fontSize: 14, color: 'var(--foreground)', lineHeight: 1.5 }}>
          <NoteMarkdown text={note.text} />
        </div>
      )}

      {!editing && (canManage || note.isPinned) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {note.isPinned && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--primary)',
              }}
            >
              <PinIcon filled size={11} strokeWidth={1.6} /> Fijada
            </span>
          )}
          {canManage && (
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
              <button
                onClick={onTogglePin}
                disabled={saving}
                title={note.isPinned ? 'Desfijar nota' : 'Fijar nota'}
                aria-label={note.isPinned ? 'Desfijar nota' : 'Fijar nota'}
                style={{ ...actionButtonStyle, color: note.isPinned ? 'var(--primary)' : 'var(--muted-foreground)' }}
              >
                <PinIcon filled={note.isPinned} size={14} strokeWidth={1.6} />
              </button>
              <button
                onClick={startEdit}
                disabled={saving}
                title="Editar nota"
                aria-label="Editar nota"
                style={actionButtonStyle}
              >
                <PencilNoteIcon />
              </button>
              <button
                onClick={onDelete}
                disabled={saving}
                title="Eliminar nota"
                aria-label="Eliminar nota"
                style={actionButtonStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'var(--destructive)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'var(--muted-foreground)';
                }}
              >
                <TrashNoteIcon />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default NoteCard;
